import { useState } from 'react';

import LoadingButton from '@mui/lab/LoadingButton';
import Stack from '@mui/material/Stack';

import { notification } from '@webclient/components/notification';
import RHFCheckbox from '@webclient/components/react-hook-form/RHFCheckbox';
import useLogin from '@webclient/components/user-authentication/hooks/useLogin';
import useSendResetToken from '@webclient/components/user-authentication/hooks/useSendResetToken';
import useUserAuth from '@webclient/components/user-authentication/hooks/useUserAuth';
import { enterPasswordSchema } from '@webclient/components/user-authentication/schemas';
import BaseStep from '@webclient/components/user-authentication/sections/steps/components/base-step';
import PasswordField from '@webclient/components/user-authentication/sections/steps/components/password-field';
import StyledLinkLoadingButton from '@webclient/components/user-authentication/sections/steps/styles';
import ValidateCode from '@webclient/components/user-authentication/sections/steps/validate-code';

import { setSession } from '@webclient/lib/jwt';

import { queryClient } from '@webclient/providers/ReactQueryProvider';

import useAcceptInvitation from '@webclient/hooks/useAcceptInvitation';
import useAppDispatch from '@webclient/hooks/useAppDispatch';
import useQueryParams from '@webclient/hooks/useQueryParams';
import useUpdateMe from '@webclient/hooks/useUpdateMe';
import useWorkspace from '@webclient/hooks/useWorkspace';

import { setAttemptedToLogin } from '@webclient/state/slices/auth';

import UserLoginIcon from '~icons/knowz-iconify/user-login';

import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslate } from '@tolgee/react';
import { isAxiosError } from 'axios';
import { useForm } from 'react-hook-form';

const defaultValues = {
  password: '',
  rememberMe: false,
};

type EnterPasswordFormType = {
  password: string;
  rememberMe: boolean;
};

export default function EnterPassword() {
  const [showResetPasswordValidator, setShowResetPasswordValidator] =
    useState(false);
  const dispatch = useAppDispatch();

  const { email } = useUserAuth();
  const { handleUpdateMe } = useUpdateMe();

  const { mutate: mutateLogin, isPending: isLoginPending } = useLogin({
    onSuccess: async ({ data }) => {
      window.localStorage.removeItem('fingerPrintId');
      resetWorkspaceToIndividualRole();
      setSession(data.token);
      handleUpdateMe(data.token);
      dispatch(setAttemptedToLogin(true));
      removeQueryParamsByKeys(['action', 'subscription']);

      if (invitationToken) {
        await mutateAcceptInvitationAsync({
          token: invitationToken,
        });
      }

      queryClient.refetchQueries();
      notification.success({
        message: t(
          'response.success.login',
          'You have successfully logged in.',
        ),
      });
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === 401) {
        notification.error({
          message: t(
            'response.errors.code.401',
            'Invalid credentials. Please try again.',
          ),
        });
        return;
      }
      notification.error({
        message: t(
          'response.errors.code.520',
          'Something went wrong. Please try again later.',
        ),
      });
      console.error(error);
    },
  });

  const { t } = useTranslate();

  const { removeQueryParamsByKeys, getQueryParamByKey } = useQueryParams();

  const methods = useForm<EnterPasswordFormType>({
    resolver: yupResolver(enterPasswordSchema(t)),
    defaultValues,
  });

  const { resetWorkspaceToIndividualRole } = useWorkspace();

  const { mutateAcceptInvitationAsync } = useAcceptInvitation({
    onSuccess: () => {
      notification.success({
        message: t(
          'page.knowledgeBase.member.membership.accepted',
          'You have successfully accepted this invitation.',
        ),
      });
    },
  });

  const {
    formState: { errors, isDirty, isSubmitting },
    handleSubmit,
  } = methods;

  const isBusy = isLoginPending || isSubmitting;

  const invitationToken = getQueryParamByKey<string | null>('token');

  const {
    mutateAsync: mutateAsyncSendResetToken,
    isPending: isSendResetTokenPending,
    isError: isSendResetTokenError,
  } = useSendResetToken();

  async function onSubmit(data: EnterPasswordFormType) {
    mutateLogin({
      email,
      password: data.password,
      auth_type: 'email',
      remember_me: data.rememberMe,
    });
  }

  async function handleResetPassword() {
    await mutateAsyncSendResetToken({ email, request_type: 'code' });
    if (isSendResetTokenError) return;
    setShowResetPasswordValidator(true);
  }

  return showResetPasswordValidator ? (
    <ValidateCode />
  ) : (
    <BaseStep
      slots={{
        header: {
          withBackButton: true,
          heading: t('layout.auth.steps.enterPassword.label', 'Welcome Back!'),
          description: t(
            'layout.auth.steps.enterPassword.form.password.label',
            'Enter your password',
          ),
        },

        form: {
          methods: methods,
          onSubmit: handleSubmit(onSubmit),
        },
      }}
    >
      <PasswordField
        placeholder={t(
          'layout.auth.desktop.modal.steps.enterPassword.form.password.label',
          'Enter your password',
        )}
        name="password"
        disabled={isBusy}
        helperText={errors.password?.message}
      />

      <Stack
        direction="row"
        justifyContent="space-between"
      >
        <RHFCheckbox
          name="rememberMe"
          label={t('layout.auth.buttons.rememberMe', 'Remember me')}
        />

        <StyledLinkLoadingButton
          onClick={handleResetPassword}
          disabled={isSendResetTokenPending}
          loading={isSendResetTokenPending}
        >
          {t('layout.auth.buttons.forgotPassword', 'Forgot password?')}
        </StyledLinkLoadingButton>
      </Stack>

      <LoadingButton
        size="xlarge"
        onClick={handleSubmit(onSubmit)}
        variant="contained"
        fullWidth
        disabled={!isDirty || isBusy}
        loading={isBusy}
        startIcon={<UserLoginIcon fontSize={20} />}
      >
        {t('layout.auth.buttons.login', 'login')}
      </LoadingButton>
    </BaseStep>
  );
}
